/* global Sanki */
/**
 * Sanki — helpers.js
 * Lightweight, dependency-free utilities used across the theme.
 */
(function (global) {
  'use strict';

  const Sanki = (global.Sanki = global.Sanki || {});
  const H = (Sanki.helpers = Sanki.helpers || {});

  /**
   * Format a price in the shop's currency using the shop's money format.
   * Shopify money_format strings use `{{amount}}`, `{{amount_no_decimals}}`,
   * `{{amount_with_comma_separator}}`, and `{{amount_no_decimals_with_comma_separator}}`.
   * @param {number} cents — price in cents
   * @param {string} [format] — optional override format
   * @returns {string}
   */
  H.money = function money(cents, format) {
    const fmt = format || (Sanki.shop && Sanki.shop.moneyFormat) || '${{amount}}';
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholder);
    if (!match) return fmt;

    const fmtNumber = (amount, decimals, thousandsSep, decimalSep) => {
      if (isNaN(amount) || amount == null) return '0';
      const n = (amount / 100.0).toFixed(decimals);
      const parts = n.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return decimals ? whole + decimalSep + parts[1] : whole;
    };

    let replacement;
    switch (match[1]) {
      case 'amount':                                 replacement = fmtNumber(value, 2, ',', '.'); break;
      case 'amount_no_decimals':                     replacement = fmtNumber(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator':            replacement = fmtNumber(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':replacement = fmtNumber(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':       replacement = fmtNumber(value, 2, "'", '.'); break;
      default:                                       replacement = fmtNumber(value, 2, ',', '.');
    }
    return fmt.replace(placeholder, replacement);
  };

  /**
   * Debounce — trailing edge by default.
   */
  H.debounce = function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  /**
   * Throttle — leading + trailing.
   */
  H.throttle = function throttle(fn, wait) {
    let last = 0, t;
    return function throttled(...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        if (t) { clearTimeout(t); t = null; }
        last = now; fn.apply(this, args);
      } else if (!t) {
        t = setTimeout(() => { last = Date.now(); t = null; fn.apply(this, args); }, remaining);
      }
    };
  };

  H.escape = function escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  /**
   * Emit a bubbling CustomEvent.
   */
  H.emit = function emit(el, name, detail) {
    const target = el || document;
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  };

  /**
   * Show a toast at the bottom. Auto-dismisses.
   */
  H.toast = function toast(message, ms) {
    const el = document.getElementById('SkToast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-open');
    clearTimeout(H.toast._t);
    H.toast._t = setTimeout(() => el.classList.remove('is-open'), ms || 2600);
  };

  /**
   * In-viewport check (for fallback when IntersectionObserver is missing).
   */
  H.inViewport = function inViewport(el, margin) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const m = margin || 0;
    return r.bottom >= -m && r.top <= (window.innerHeight + m);
  };

  /**
   * Focus trap helper — returns a disposer.
   */
  H.trapFocus = function trapFocus(container) {
    const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const prev = document.activeElement;
    const getNodes = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter(n => !n.hasAttribute('hidden'));
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = getNodes();
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    const nodes = getNodes();
    if (nodes.length) nodes[0].focus();
    return function dispose() {
      container.removeEventListener('keydown', onKey);
      if (prev && prev.focus) prev.focus();
    };
  };

  /**
   * Safe JSON parse (returns fallback on error).
   */
  H.parseJSON = function parseJSON(str, fallback) {
    try { return JSON.parse(str); } catch (_) { return fallback === undefined ? null : fallback; }
  };

  /**
   * LocalStorage with JSON encoding and silent error handling.
   */
  H.storage = {
    get(key, fallback) {
      try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
      catch (_) { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); return true; }
      catch (_) { return false; }
    },
    remove(key) {
      try { localStorage.removeItem(key); } catch (_) {}
    }
  };
})(typeof window !== 'undefined' ? window : this);
